import React, { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

const STATUS_BADGES = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
}

const ENTITY_LABELS = {
  rule: 'Rule',
  smr: 'SMR',
  ecdd: 'ECDD',
  access: 'Access Grant',
  case: 'Case',
  governance: 'Governance',
}

export default function PendingApprovals() {
  const { user, can } = useAuth()
  const [pending, setPending] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deciding, setDeciding] = useState(null)
  const [comment, setComment] = useState('')
  const [filter, setFilter] = useState('all')

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch from both platform and tenant approval queues
      const entityFilter = filter === 'all' ? '' : `?entity_type=${filter}`
      const [platformRes, tenantRes] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/approvals/pending${entityFilter}`)).catch(() => ({ ok: false })),
        authFetch(getLocalApiUrl(`/api/tenant-approvals/pending${entityFilter}`)).catch(() => ({ ok: false })),
      ])
      
      const platformData = platformRes.ok ? await platformRes.json().catch(() => ({})) : {}
      const tenantData = tenantRes.ok ? await tenantRes.json().catch(() => ({})) : {}
      
      // Merge results, marking source for proper API calls
      const platformItems = (platformData.data || []).map(r => ({ ...r, _source: 'platform' }))
      const tenantItems = (tenantData.data || []).map(r => ({ ...r, _source: 'tenant' }))
      
      const merged = [...tenantItems, ...platformItems].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
      
      setPending(merged)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => { load() }, [load])

  const handleDecide = async (requestId, decision, source = 'platform') => {
    try {
      setDeciding(requestId)
      // Use tenant endpoint for tenant approvals, platform endpoint for others
      const endpoint = source === 'tenant' 
        ? `/api/tenant-approvals/${requestId}/decide`
        : `/api/approvals/${requestId}/decide`
      const res = await authFetch(getLocalApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, comment: comment || null }),
      })
      const j = await res.json()
      if (!j.success) throw new Error(j.error || 'Decision failed')
      setComment('')
      setDeciding(null)
      await load()
    } catch (e) {
      setError(e.message)
      setDeciding(null)
    }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—'
  const canDecide = can('admin/approvals', 'approve') || can('admin/approvals', 'manage') || can('approvals', 'decide')

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Pending Approvals</h1>
            <p className="text-xs text-gray-500">Review and approve or reject pending requests across the system</p>
          </div>
          <div className="flex items-center gap-3">
            <select className="select-field" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Types</option>
              {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button className="btn-secondary" onClick={load} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </div>

      {pending.length === 0 && !loading && (
        <div className="card text-center py-8 text-gray-500">
          No pending approvals for you to review.
        </div>
      )}

      <div className="space-y-3">
        {pending.map((req) => (
          <div key={req.id} className="card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGES[req.status] || STATUS_BADGES.pending}`}>
                    {req.status}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {ENTITY_LABELS[req.entity_type] || req.entity_type}
                  </span>
                  <span className="text-xs text-gray-500">#{req.id}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {req.entity_type}:{req.action} — {req.entity_id}
                </div>
                {req.request_data && (
                  <div className="mt-1 text-xs text-gray-500">
                    {req.request_data.name && <span className="font-medium">{req.request_data.name}</span>}
                    {req.request_data.rule_id && <span> (Rule: {req.request_data.rule_id})</span>}
                    {req.request_data.member_name && <span className="font-medium">{req.request_data.member_name}</span>}
                    {req.request_data.decision && <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{req.request_data.decision.replace(/_/g, ' ')}</span>}
                    {req.request_data.rationale && <p className="mt-1 text-gray-400 italic truncate max-w-md">{req.request_data.rationale}</p>}
                  </div>
                )}
                <div className="mt-1 text-xs text-gray-400">
                  Requested by <span className="font-medium">{req.requested_by}</span> on {formatDate(req.created_at)}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  Approvals: {req.approvals_collected} / {req.required_approvals}
                </div>
                {req.policy_description && (
                  <div className="mt-1 text-xs text-gray-400 italic">{req.policy_description}</div>
                )}
              </div>

              <div className="ml-4 flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Comment (optional)"
                    className="input-field text-xs w-48"
                    value={deciding === req.id ? comment : ''}
                    onChange={(e) => { setDeciding(req.id); setComment(e.target.value) }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {canDecide ? (
                    <>
                      <button
                        className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                        onClick={() => handleDecide(req.id, 'approved', req._source || 'platform')}
                        disabled={deciding === req.id && deciding !== null}
                      >
                        Approve
                      </button>
                      <button
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                        onClick={() => handleDecide(req.id, 'rejected', req._source || 'platform')}
                        disabled={deciding === req.id && deciding !== null}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">View only — approval permission required</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
